import Dexie, { type EntityTable, type Transaction } from "dexie";

import type {
  BackupStateRecord,
  CheckInRecord,
  DiaryEntryRecord,
  FoodRecord,
  GoalPhaseRecord,
  MetadataRecord,
  NutrientGoalRecord,
  OnboardingWeightReferenceRecord,
  PersistedSnapshotV1,
  ProfileRecord,
  RecipeRecord,
  SettingsRecord,
  WeekPlanRecord,
  WeightEntryRecord,
} from "../records";
import { version1Stores } from "./migrations/v1";
import { version2Stores } from "./migrations/v2";
import { version3Stores } from "./migrations/v3";

export const DATABASE_NAME = "diet-tracker";
export const DATABASE_SCHEMA_VERSION = 3;

const TABLE_NAMES = Object.keys(version3Stores) as Array<keyof PersistedSnapshotV1>;

export class DietTrackerDatabase extends Dexie {
  profiles!: EntityTable<ProfileRecord, "id">;
  settings!: EntityTable<SettingsRecord, "id">;
  goalPhases!: EntityTable<GoalPhaseRecord, "id">;
  nutrientGoals!: EntityTable<NutrientGoalRecord, "id">;
  foods!: EntityTable<FoodRecord, "id">;
  recipes!: EntityTable<RecipeRecord, "id">;
  diaryEntries!: EntityTable<DiaryEntryRecord, "id">;
  weightEntries!: EntityTable<WeightEntryRecord, "date">;
  onboardingReferences!: EntityTable<OnboardingWeightReferenceRecord, "id">;
  weekPlans!: EntityTable<WeekPlanRecord, "id">;
  checkIns!: EntityTable<CheckInRecord, "id">;
  backupState!: EntityTable<BackupStateRecord, "id">;
  metadata!: EntityTable<MetadataRecord, "key">;

  constructor(name = DATABASE_NAME) {
    super(name);
    this.version(1).stores(version1Stores);
    this.version(2).stores(version2Stores);
    this.version(DATABASE_SCHEMA_VERSION).stores(version3Stores);
  }

  async exportSnapshot(): Promise<PersistedSnapshotV1> {
    return this.transaction("r", this.tables, async () => ({
      profiles: await this.profiles.toArray(),
      settings: await this.settings.toArray(),
      goalPhases: await this.goalPhases.toArray(),
      nutrientGoals: await this.nutrientGoals.toArray(),
      foods: await this.foods.toArray(),
      recipes: await this.recipes.toArray(),
      diaryEntries: await this.diaryEntries.toArray(),
      weightEntries: await this.weightEntries.toArray(),
      onboardingReferences: await this.onboardingReferences.toArray(),
      weekPlans: await this.weekPlans.toArray(),
      checkIns: await this.checkIns.toArray(),
      backupState: await this.backupState.toArray(),
      metadata: await this.metadata.toArray(),
    }));
  }

  /**
   * Replaces all logical data in one IndexedDB transaction. Validation and backup
   * migration must finish before calling this method; any write failure rolls the
   * transaction back, preserving the previous database.
   */
  async replaceFromSnapshot(snapshot: PersistedSnapshotV1): Promise<void> {
    await this.transaction("rw", this.tables, async (transaction: Transaction) => {
      for (const tableName of TABLE_NAMES) {
        await transaction.table(tableName).clear();
      }

      await this.profiles.bulkPut(snapshot.profiles);
      await this.settings.bulkPut(snapshot.settings);
      await this.goalPhases.bulkPut(snapshot.goalPhases);
      await this.nutrientGoals.bulkPut(snapshot.nutrientGoals);
      await this.foods.bulkPut(snapshot.foods);
      await this.recipes.bulkPut(snapshot.recipes);
      await this.diaryEntries.bulkPut(snapshot.diaryEntries);
      await this.weightEntries.bulkPut(snapshot.weightEntries);
      await this.onboardingReferences.bulkPut(snapshot.onboardingReferences);
      await this.weekPlans.bulkPut(snapshot.weekPlans);
      await this.checkIns.bulkPut(snapshot.checkIns);
      await this.backupState.bulkPut(snapshot.backupState);
      await this.metadata.bulkPut(snapshot.metadata);
    });
  }

  async runUserDataTransaction<T>(work: () => Promise<T>): Promise<T> {
    return this.transaction("rw", this.tables, async () => {
      const result = await work();
      const existing = await this.backupState.get("google-drive");
      await this.backupState.put({
        id: "google-drive",
        deviceId: existing?.deviceId ?? createDeviceId(),
        localRevision: (existing?.localRevision ?? 0) + 1,
        lastBackupId: existing?.lastBackupId,
        lastBackedUpRevision: existing?.lastBackedUpRevision,
        lastBackupAt: existing?.lastBackupAt,
        pending: true,
      });
      return result;
    });
  }
}

function createDeviceId(): string {
  return globalThis.crypto?.randomUUID?.() ??
    `device-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}
